import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

export default function LoginPage() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const navigate = useNavigate();
  const { login, getAuthHeaders } = useAuth();

  const BASE = 'https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app';
  const api = async (path, body) => {
    const res = await fetch(`${BASE}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
    return { res, data: await res.json() };
  };

  const reset = () => { setPhoneNumber(''); setOtpCode(''); setPassword(''); setNewPassword(''); setShowOtpInput(false); setShowPasswordInput(false); setError(''); setSuccess(''); setIsLoading(false); setResettingPassword(false); };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    if (!/^\d{11}$/.test(phoneNumber)) { setError('Please enter a valid 11-digit mobile number.'); setIsLoading(false); return; }
    if (!password.trim()) { setError('Password cannot be empty.'); setIsLoading(false); return; }
    try {
      const { res, data } = await api('/login', { phone_number: phoneNumber, password });
      if (res.ok) { setSuccess(data.message); login(data.access_token, data.user_info); navigate('/'); }
      else { setError(data.error || 'Login failed.'); }
    } catch { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setShowOtpInput(false); setShowPasswordInput(false); setIsLoading(true);
    if (!/^\d{11}$/.test(phoneNumber)) { setError('Please enter a valid 11-digit mobile number.'); setIsLoading(false); return; }
    try {
      const { res, data } = await api('/send_otp', { phone_number: phoneNumber });
      if (res.ok) { setSuccess(data.message + ' Please wait for your OTP.'); setShowOtpInput(true); }
      else { setError(data.error || 'Failed to send OTP.'); }
    } catch { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    if (!otpCode.trim()) { setError('OTP code cannot be empty.'); setIsLoading(false); return; }
    try {
      const { res, data } = await api('/verify_otp', { phone_number: phoneNumber, otp_code: otpCode });
      if (res.ok) { setSuccess(data.message); if (data.is_registered) { login(data.access_token, data.user_info); navigate('/'); } else { setShowPasswordInput(true); } }
      else { setError(data.error || 'OTP verification failed.'); }
    } catch { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleCompleteRegistration = async (e, autoGenerate = false) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    if (!autoGenerate && !password.trim()) { setError('Password cannot be empty.'); setIsLoading(false); return; }
    try {
      const body = { phone_number: phoneNumber };
      if (!autoGenerate) body.password = password;
      const path = autoGenerate ? '/register_with_auto_password' : '/complete_registration';
      const { res, data } = await api(path, body);
      if (res.ok) { setSuccess(data.message); login(data.access_token, data.user_info); navigate('/'); }
      else { setError(data.error || 'Registration failed.'); }
    } catch { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleForgotOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    if (!/^\d{11}$/.test(phoneNumber)) { setError('Please enter a valid 11-digit mobile number.'); setIsLoading(false); return; }
    try {
      const { res, data } = await api('/forgot_password_send_otp', { phone_number: phoneNumber });
      if (res.ok) { setSuccess(data.message + ' Please wait for your OTP.'); setShowOtpInput(true); }
      else { setError(data.error || 'Failed to send OTP.'); }
    } catch { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setResettingPassword(true);
    if (!otpCode.trim() || !newPassword.trim()) { setError('Both OTP and new password are required.'); setResettingPassword(false); return; }
    try {
      const { res, data } = await api('/reset_password_verify_otp', { phone_number: phoneNumber, otp_code: otpCode, new_password: newPassword });
      if (res.ok) { setSuccess(data.message + ' You can now login with your new password.'); setIsForgotPasswordMode(false); setIsLoginMode(true); reset(); }
      else { setError(data.error || 'Password reset failed.'); }
    } catch { setError('Unable to connect to server.'); }
    finally { setResettingPassword(false); }
  };

  const switchToRegister = () => { setIsLoginMode(false); setIsForgotPasswordMode(false); reset(); };
  const switchToLogin = () => { setIsLoginMode(true); setIsForgotPasswordMode(false); reset(); };
  const switchToForgot = () => { setIsForgotPasswordMode(true); setIsLoginMode(false); reset(); };

  const modeTitle = isLoginMode ? 'Sign In' : isForgotPasswordMode ? 'Reset Password' : 'Create Account';
  const busy = isLoading || resettingPassword;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&display=swap');
        .login-page { min-height: 100vh; background: #f5f5f5; display: flex; align-items: center; justify-content: center; padding: 80px 16px 40px; font-family: 'Hind Siliguri', sans-serif; }
        .login-box { width: 100%; max-width: 400px; }
        .login-logo { text-align: center; margin-bottom: 24px; }
        .login-logo img { height: 36px; }
        .login-card { background: #fff; border: 1px solid #e8e8e8; border-radius: 4px; padding: 28px 28px 24px; }
        .login-title { font-size: 20px; font-weight: 700; color: #282c3f; margin-bottom: 20px; }
        .form-group { margin-bottom: 14px; }
        .form-label { display: block; font-size: 13px; font-weight: 600; color: #282c3f; margin-bottom: 5px; }
        .form-input { width: 100%; padding: 10px 14px; border: 1.5px solid #e8e8e8; border-radius: 4px; font-size: 14px; font-family: inherit; color: #282c3f; outline: none; transition: border-color 0.2s; box-sizing: border-box; background: #fff; }
        .form-input:focus { border-color: #282c3f; }
        .form-input:disabled { background: #fafafa; color: #94a3b8; cursor: not-allowed; }
        .input-wrap { position: relative; }
        .input-wrap .form-input { padding-right: 44px; }
        .eye-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #94a3b8; padding: 2px; display: flex; align-items: center; }
        .eye-btn:hover { color: #282c3f; }
        .btn-primary { width: 100%; padding: 11px; background: #282c3f; color: #fff; border: none; border-radius: 4px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; letter-spacing: 0.02em; transition: background 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .btn-primary:hover:not(:disabled) { background: #1a1d2b; }
        .btn-primary:disabled { background: #94a3b8; cursor: not-allowed; }
        .btn-outline { width: 100%; padding: 11px; background: #fff; color: #282c3f; border: 1.5px solid #e8e8e8; border-radius: 4px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; transition: background 0.2s, border-color 0.2s; }
        .btn-outline:hover:not(:disabled) { background: #f5f5f5; border-color: #282c3f; }
        .btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }
        .divider { display: flex; align-items: center; gap: 12px; margin: 16px 0; }
        .divider-line { flex: 1; height: 1px; background: #f0f0f0; }
        .divider-text { font-size: 12px; color: #94a3b8; white-space: nowrap; }
        .section-sep { border-top: 1px solid #f0f0f0; margin: 18px 0; padding-top: 18px; }
        .section-label { font-size: 13px; font-weight: 600; color: #282c3f; margin-bottom: 14px; }
        .alert { padding: 10px 14px; border-radius: 4px; font-size: 13px; font-weight: 500; margin-top: 14px; }
        .alert-error { background: #fff0f3; border: 1px solid #ffd0d8; color: #c0392b; }
        .alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
        .alert-info { background: #f0f9ff; border: 1px solid #bae6fd; color: #0369a1; }
        .forgot-link { font-size: 13px; color: #ff3f6c; cursor: pointer; background: none; border: none; padding: 0; font-family: inherit; margin-top: 8px; display: inline-block; }
        .forgot-link:hover { text-decoration: underline; }
        .bottom-section { margin-top: 16px; }
        .bottom-divider { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .footer-links { text-align: center; margin-top: 20px; }
        .footer-link { font-size: 12px; color: #94a3b8; margin: 0 8px; cursor: pointer; }
        .footer-link:hover { color: #282c3f; text-decoration: underline; }
        .footer-copy { font-size: 11px; color: #94a3b8; text-align: center; margin-top: 12px; }
        .spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="login-page">
        <div className="login-box">
          {/* Logo */}
          <div className="login-logo">
            <a href="/">
              <img src="https://fabrilife.com/image-gallery/fabrilife.svg" alt="Logo" />
            </a>
          </div>

          <div className="login-card">
            <div className="login-title">{modeTitle}</div>

            {/* ── LOGIN ── */}
            {isLoginMode && (
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">Mobile Number</label>
                  <input className="form-input" type="tel" placeholder="01XXXXXXXXX" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} disabled={busy} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <div className="input-wrap">
                    <input className="form-input" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} disabled={busy} required />
                    <button type="button" className="eye-btn" onClick={() => setShowPassword(p => !p)}><EyeIcon open={showPassword} /></button>
                  </div>
                </div>
                <button className="btn-primary" type="submit" disabled={busy} style={{ marginTop: 4 }}>
                  {busy ? <><span className="spinner" />Signing in…</> : 'Sign In'}
                </button>
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="forgot-link" onClick={switchToForgot}>Forgot your password?</button>
                </div>
              </form>
            )}

            {/* ── REGISTER ── */}
            {!isLoginMode && !isForgotPasswordMode && (
              <>
                <form onSubmit={handleSendOtp}>
                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <input className="form-input" type="tel" placeholder="01XXXXXXXXX" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} disabled={showOtpInput || showPasswordInput || busy} required />
                  </div>
                  <button className="btn-primary" type="submit" disabled={showOtpInput || showPasswordInput || busy}>
                    {busy && !showOtpInput ? <><span className="spinner" />Sending…</> : 'Send OTP'}
                  </button>
                </form>

                {showOtpInput && (
                  <form onSubmit={handleVerifyOtp}>
                    <div className="section-sep">
                      <div className="section-label">Enter OTP</div>
                      <div className="form-group">
                        <label className="form-label">OTP Code</label>
                        <input className="form-input" type="text" placeholder="Enter OTP sent to your phone" value={otpCode} onChange={e => setOtpCode(e.target.value)} disabled={showPasswordInput || busy} required />
                      </div>
                      <button className="btn-primary" type="submit" disabled={showPasswordInput || busy}>
                        {busy ? <><span className="spinner" />Verifying…</> : 'Verify OTP'}
                      </button>
                    </div>
                  </form>
                )}

                {showPasswordInput && (
                  <div className="section-sep">
                    <div className="section-label">Set Password</div>
                    <div className="form-group">
                      <label className="form-label">New Password</label>
                      <div className="input-wrap">
                        <input className="form-input" type={showNewPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} disabled={busy} />
                        <button type="button" className="eye-btn" onClick={() => setShowNewPassword(p => !p)}><EyeIcon open={showNewPassword} /></button>
                      </div>
                    </div>
                    <button className="btn-primary" type="button" onClick={e => handleCompleteRegistration(e, false)} disabled={busy} style={{ marginBottom: 10 }}>
                      {busy ? <><span className="spinner" />Registering…</> : 'Register with Password'}
                    </button>
                    <div className="divider"><div className="divider-line" /><span className="divider-text">or</span><div className="divider-line" /></div>
                    <button className="btn-outline" type="button" onClick={e => handleCompleteRegistration(e, true)} disabled={busy}>
                      Register with Auto-generated Password
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── FORGOT PASSWORD ── */}
            {isForgotPasswordMode && (
              <>
                <form onSubmit={handleForgotOtp}>
                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <input className="form-input" type="tel" placeholder="01XXXXXXXXX" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} disabled={showOtpInput || busy} required />
                  </div>
                  <button className="btn-primary" type="submit" disabled={showOtpInput || busy}>
                    {busy && !showOtpInput ? <><span className="spinner" />Sending…</> : 'Send OTP'}
                  </button>
                </form>

                {showOtpInput && (
                  <form onSubmit={handleResetPassword}>
                    <div className="section-sep">
                      <div className="section-label">Reset Password</div>
                      <div className="form-group">
                        <label className="form-label">OTP Code</label>
                        <input className="form-input" type="text" placeholder="Enter OTP sent to your phone" value={otpCode} onChange={e => setOtpCode(e.target.value)} disabled={resettingPassword} required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">New Password</label>
                        <div className="input-wrap">
                          <input className="form-input" type={showNewPassword ? 'text' : 'password'} placeholder="Enter your new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} disabled={resettingPassword} required />
                          <button type="button" className="eye-btn" onClick={() => setShowNewPassword(p => !p)}><EyeIcon open={showNewPassword} /></button>
                        </div>
                      </div>
                      <button className="btn-primary" type="submit" disabled={resettingPassword}>
                        {resettingPassword ? <><span className="spinner" />Resetting…</> : 'Reset Password'}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}

            {/* Alerts */}
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}
            {busy && !error && !success && <div className="alert alert-info">Please wait…</div>}
          </div>

          {/* Bottom toggle */}
          <div className="bottom-section">
            <div className="bottom-divider">
              <div className="divider-line" />
              <span className="divider-text">{isLoginMode ? 'New here?' : 'Already have an account?'}</span>
              <div className="divider-line" />
            </div>
            <button className="btn-outline" onClick={isLoginMode ? switchToRegister : switchToLogin}>
              {isLoginMode ? 'Create Account' : 'Sign In'}
            </button>
          </div>

          {/* Footer */}
          <div className="footer-links">
            <a href="/terms" className="footer-link">Terms of Use</a>
            <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
            <a href="/refund-policy" className="footer-link">Help</a>
          </div>
          <div className="footer-copy">© 2024 Aman LifeStyle. All rights reserved.</div>
        </div>
      </div>
    </>
  );
}